import React, { useEffect, useState } from "react";
import JSZip from "jszip";
import DOMPurify from "dompurify";
import { supabase } from "../supabase";

const CARDINAL = "#B83E26";
const ARTICLE_BUCKET = process.env.REACT_APP_SUPABASE_ARTICLE_BUCKET || "images";
const ARTICLE_STORAGE_PREFIX = process.env.REACT_APP_SUPABASE_ARTICLE_PREFIX || "articles";
const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "webp", "gif", "heic", "heif"];

const mimeFromExtension = (extension) => {
  switch (extension) {
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "webp":
      return "image/webp";
    case "gif":
      return "image/gif";
    case "heic":
      return "image/heic";
    case "heif":
      return "image/heif";
    default:
      return "application/octet-stream";
  }
};

const extensionFromMime = (mimeType) => {
  if (!mimeType) return "";
  const lower = mimeType.toLowerCase();
  if (lower.includes("zip")) return ".zip";
  if (lower.includes("jpeg")) return ".jpg";
  if (lower.includes("png")) return ".png";
  if (lower.includes("webp")) return ".webp";
  if (lower.includes("gif")) return ".gif";
  if (lower.includes("heic")) return ".heic";
  if (lower.includes("heif")) return ".heif";
  return "";
};

const validateAndSanitizeContent = (content) => {
  if (!content || typeof content !== "string") {
    return { isValid: false, sanitized: "", error: "Content must be a non-empty string." };
  }

  if (content.length > 1000000) {
    return { isValid: false, sanitized: "", error: "Content exceeds maximum length of 1MB." };
  }

  const dangerousPatterns = [
    /<script[^>]*>.*?<\/script>/gi,
    /javascript:/gi,
    /on\w+\s*=/gi,
    /<iframe[^>]*>/gi,
    /<object[^>]*>/gi,
    /<embed[^>]*>/gi,
    /<link[^>]*>/gi,
    /<meta[^>]*>/gi,
    /data:text\/html/gi,
    /vbscript:/gi,
  ];

  for (const pattern of dangerousPatterns) {
    if (pattern.test(content)) {
      return { 
        isValid: false, 
        sanitized: "", 
        error: "Content contains potentially malicious code. Please remove scripts, event handlers, or embedded content." 
      };
    }
  }

  const sanitized = DOMPurify.sanitize(content, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'a', 'img', 'div', 'span'
    ],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class'],
    ALLOW_DATA_ATTR: false,
  });

  return { isValid: true, sanitized, error: null };
};

const tabButtonStyle = (active) => ({
  borderRadius: "999px",
  padding: "8px 18px",
  border: `1px solid ${CARDINAL}`,
  backgroundColor: active ? CARDINAL : "#fff",
  color: active ? "#fff" : CARDINAL,
  fontWeight: 700,
  cursor: "pointer",
  boxShadow: active ? "0 6px 16px rgba(0,0,0,0.12)" : "none",
});

function Dashboard() {
  const [session, setSession] = useState(null);
  const [activeTab, setActiveTab] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const allowedDomains = ["usc.edu", "alumni.usc.edu"];

  const isAllowedEmail = (value) => {
    const domain = value.split("@")[1]?.toLowerCase() || "";
    return allowedDomains.includes(domain);
  };
  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [articleNotice, setArticleNotice] = useState("");
  const [uploading, setUploading] = useState(false);
  const [articleTitle, setArticleTitle] = useState("");
  const [articleDescription, setArticleDescription] = useState("");
  const [articleFile, setArticleFile] = useState(null);
  const [uploadMode, setUploadMode] = useState("image");
  const [contentBlocks, setContentBlocks] = useState([{ id: crypto.randomUUID(), type: "text", content: "" }]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [draggedBlockIndex, setDraggedBlockIndex] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session ?? null);
    });

    const { data } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession ?? null);
    });

    return () => {
      data.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (session) {
      loadArticles();
    } else {
      setArticles([]);
    }
  }, [session]);

  const loadArticles = async () => {
    setArticlesLoading(true);
    setArticleNotice("");
    const { data, error } = await supabase
      .from("article")
      .select(
        "article_id, article_title, article_description, article_date, article_image ( image_id, ordinal, image ( mime_type, storage_path ) )"
      )
      .eq("author_user_id", session.user.id)
      .order("article_date", { ascending: false });

    if (error) {
      setArticleNotice(error.message);
      setArticles([]);
    } else {
      setArticles(data ?? []);
    }
    setArticlesLoading(false);
  };

  const handleAuth = async (event) => {
    event.preventDefault();
    setLoading(true);
    setMessage("");

    if (activeTab === "login") {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) setMessage(error.message);
    } else {
      if (!isAllowedEmail(email)) {
        setMessage("Please use a @usc.edu or @alumni.usc.edu email.");
        setLoading(false);
        return;
      }
      const { error } = await supabase.auth.signUp({ email, password });
      if (error) {
        setMessage(error.message);
      } else {
        setMessage("Check your email to confirm your account, then sign in.");
      }
    }

    setLoading(false);
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setMessage("");
  };

  const handleResetPassword = async () => {
    if (!email) {
      setMessage("Enter your email above to receive a reset link.");
      return;
    }
    setLoading(true);
    setMessage("");
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/dashboard`,
    });
    if (error) {
      setMessage(error.message);
    } else {
      setMessage("Check your email for a password reset link.");
    }
    setLoading(false);
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      setArticleNotice("Please upload a valid image file (JPG, PNG, WEBP, or GIF).");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setArticleNotice("Image must be smaller than 5MB.");
      return;
    }

    setUploadingImage(true);
    setArticleNotice("");

    try {
      const imageId = crypto.randomUUID();
      const extension = file.name.split(".").pop()?.toLowerCase() ?? "jpg";
      const fileName = `${imageId}.${extension}`;
      const imagePath = `${ARTICLE_STORAGE_PREFIX}/temp/${session.user.id}/${fileName}`;
      const mimeType = mimeFromExtension(extension);

      const { error: uploadError } = await supabase.storage
        .from(ARTICLE_BUCKET)
        .upload(imagePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from(ARTICLE_BUCKET)
        .getPublicUrl(imagePath);

      const imageUrl = urlData.publicUrl;
      
      // Add image as a new block at the end
      const newBlock = {
        id: crypto.randomUUID(),
        type: "image",
        imageId: imageId,
        url: imageUrl,
        path: imagePath,
        mimeType: mimeType,
        sizeBytes: file.size
      };
      
      setContentBlocks([...contentBlocks, newBlock]);
      setArticleNotice("Image uploaded! Drag to reorder.");
    } catch (error) {
      setArticleNotice(error.message || "Failed to upload image.");
    } finally {
      setUploadingImage(false);
    }
  };

  const addTextBlock = () => {
    const newBlock = { id: crypto.randomUUID(), type: "text", content: "" };
    setContentBlocks([...contentBlocks, newBlock]);
  };

  const updateBlockContent = (blockId, content) => {
    setContentBlocks(contentBlocks.map(block => 
      block.id === blockId ? { ...block, content } : block
    ));
  };

  const deleteBlock = (blockId) => {
    setContentBlocks(contentBlocks.filter(block => block.id !== blockId));
  };

  const handleDragStart = (index) => {
    setDraggedBlockIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedBlockIndex === null || draggedBlockIndex === index) return;

    const newBlocks = [...contentBlocks];
    const draggedBlock = newBlocks[draggedBlockIndex];
    newBlocks.splice(draggedBlockIndex, 1);
    newBlocks.splice(index, 0, draggedBlock);

    setContentBlocks(newBlocks);
    setDraggedBlockIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedBlockIndex(null);
  };

  const handleCreateTextArticle = async (event) => {
    event.preventDefault();
    setArticleNotice("");

    if (!articleTitle.trim()) {
      setArticleNotice("Add a title before uploading.");
      return;
    }
    if (!articleDescription.trim()) {
      setArticleNotice("Add a description before uploading.");
      return;
    }
    const textBlocks = contentBlocks.filter(b => b.type === "text" && b.content.trim());
    const imageBlocks = contentBlocks.filter(b => b.type === "image");
    
    if (textBlocks.length === 0 && imageBlocks.length === 0) {
      setArticleNotice("Add article content before uploading.");
      return;
    }

    setUploading(true);

    const { data: articleData, error: articleError } = await supabase
      .from("article")
      .insert({
        author_user_id: session.user.id,
        article_date: new Date().toISOString(),
        article_title: articleTitle.trim(),
        article_description: articleDescription.trim(),
      })
      .select("article_id")
      .single();

    if (articleError) {
      setArticleNotice(articleError.message);
      setUploading(false);
      return;
    }

    // Process and save blocks
    const uploadedImageIds = [];
    const uploadedPaths = [];
    
    try {
      // Save text blocks
      for (let i = 0; i < contentBlocks.length; i++) {
        const block = contentBlocks[i];
        
        if (block.type === "text" && block.content.trim()) {
          const validation = validateAndSanitizeContent(block.content);
          if (!validation.isValid) {
            throw new Error(`Block ${i + 1}: ${validation.error}`);
          }
          
          const { error: blockError } = await supabase.from("text_article_block").insert({
            article_id: articleData.article_id,
            content: validation.sanitized,
            ordinal: i,
          });
          
          if (blockError) throw blockError;
        } else if (block.type === "image") {
          // Move image from temp to permanent storage
          const oldPath = block.path;
          const fileName = oldPath.split('/').pop();
          const newPath = `${ARTICLE_STORAGE_PREFIX}/${articleData.article_id}/${fileName}`;
          
          const { data: fileData } = await supabase.storage
            .from(ARTICLE_BUCKET)
            .download(oldPath);
          
          if (fileData) {
            const { error: uploadError } = await supabase.storage
              .from(ARTICLE_BUCKET)
              .upload(newPath, fileData, { upsert: true });
            
            if (uploadError) throw uploadError;
            
            // Create image record
            const { error: imageRowError } = await supabase.from("image").insert({
              image_id: block.imageId,
              mime_type: block.mimeType,
              size_bytes: block.sizeBytes,
              uploaded_by: session.user.id,
              storage_path: newPath,
            });
            
            if (imageRowError) throw imageRowError;
            
            // Link image to article with ordinal
            const { error: linkError } = await supabase.from("text_article_image").insert({
              article_id: articleData.article_id,
              image_id: block.imageId,
              ordinal: i,
            });
            
            if (linkError) throw linkError;
            
            uploadedImageIds.push(block.imageId);
            uploadedPaths.push(newPath);
            
            // Delete temp file
            await supabase.storage.from(ARTICLE_BUCKET).remove([oldPath]);
          }
        }
      }
    } catch (error) {
      // Rollback: delete article, blocks, and images
      await supabase.from("text_article_block").delete().eq("article_id", articleData.article_id);
      await supabase.from("text_article_image").delete().eq("article_id", articleData.article_id);
      await supabase.from("image").delete().in("image_id", uploadedImageIds);
      await supabase.storage.from(ARTICLE_BUCKET).remove(uploadedPaths);
      await supabase.from("article").delete().eq("article_id", articleData.article_id);
      setArticleNotice(error.message || "Failed to save article.");
      setUploading(false);
      return;
    }

    setArticleTitle("");
    setArticleDescription("");
    setContentBlocks([{ id: crypto.randomUUID(), type: "text", content: "" }]);
    setArticleNotice("Text article uploaded successfully.");
    await loadArticles();
    setUploading(false);
  };

  const handleCreateArticle = async (event) => {
    event.preventDefault();
    setArticleNotice("");

    if (!articleTitle.trim()) {
      setArticleNotice("Add a title before uploading.");
      return;
    }
    if (!articleDescription.trim()) {
      setArticleNotice("Add a description before uploading.");
      return;
    }
    if (!articleFile) {
      setArticleNotice("Attach a zipped article file to upload.");
      return;
    }
    if (!articleFile.name.toLowerCase().endsWith(".zip")) {
      setArticleNotice("Only .zip files are accepted.");
      return;
    }

    setUploading(true);

    const { data: articleData, error: articleError } = await supabase
      .from("article")
      .insert({
        author_user_id: session.user.id,
        article_date: new Date().toISOString(),
        article_title: articleTitle.trim(),
        article_description: articleDescription.trim(),
      })
      .select("article_id")
      .single();

    if (articleError) {
      setArticleNotice(articleError.message);
      setUploading(false);
      return;
    }

    let uploadedImageIds = [];
    let uploadedPaths = [];
    try {
      const zip = await JSZip.loadAsync(articleFile);
      const imageEntries = Object.values(zip.files).filter((entry) => {
        if (entry.dir) return false;
        const extension = entry.name.split(".").pop()?.toLowerCase() ?? "";
        return IMAGE_EXTENSIONS.includes(extension);
      });

      for (let index = 0; index < imageEntries.length; index += 1) {
        const entry = imageEntries[index];
        const extension = entry.name.split(".").pop()?.toLowerCase() ?? "";
        const blob = await entry.async("blob");
        const imageId = crypto.randomUUID();
        const fileName = `${imageId}.${extension}`;
        const imagePath = `${ARTICLE_STORAGE_PREFIX}/${articleData.article_id}/${fileName}`;
        const mimeType = mimeFromExtension(extension);
        const uploadBlob = new Blob([blob], { type: mimeType });

        const { error: imageStorageError } = await supabase.storage
          .from(ARTICLE_BUCKET)
          .upload(imagePath, uploadBlob, { upsert: true });

        if (imageStorageError) {
          throw new Error(imageStorageError.message);
        }

        const { error: imageRowError } = await supabase.from("image").insert({
          image_id: imageId,
          mime_type: mimeType,
          size_bytes: uploadBlob.size,
          uploaded_by: session.user.id,
          storage_path: imagePath,
        });

        if (imageRowError) {
          throw new Error(imageRowError.message);
        }

        const { error: linkRowError } = await supabase.from("article_image").insert({
          article_id: articleData.article_id,
          image_id: imageId,
          ordinal: index,
        });

        if (linkRowError) {
          throw new Error(linkRowError.message);
        }

        uploadedImageIds.push(imageId);
        uploadedPaths.push(imagePath);
      }
    } catch (error) {
      await supabase.storage.from(ARTICLE_BUCKET).remove(uploadedPaths);
      await supabase.from("article_image").delete().eq("article_id", articleData.article_id);
      await supabase.from("image").delete().in("image_id", uploadedImageIds);
      await supabase.from("article").delete().eq("article_id", articleData.article_id);
      setArticleNotice(error.message || "Failed to extract images from the zip.");
      setUploading(false);
      return;
    }

    setArticleTitle("");
    setArticleDescription("");
    setArticleFile(null);
    setArticleNotice("Image article uploaded with extracted images.");
    await loadArticles();
    setUploading(false);
  };

  const startEditing = (article) => {
    setEditingId(article.article_id);
    setEditTitle(article.article_title ?? "");
    setEditDescription(article.article_description ?? "");
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditTitle("");
    setEditDescription("");
  };

  const handleUpdateArticle = async (articleId) => {
    if (!editTitle.trim()) {
      setArticleNotice("Add a title before saving.");
      return;
    }
    if (!editDescription.trim()) {
      setArticleNotice("Add a description before saving.");
      return;
    }

    setArticleNotice("");
    const { error } = await supabase
      .from("article")
      .update({
        article_title: editTitle.trim(),
        article_description: editDescription.trim(),
      })
      .eq("article_id", articleId);

    if (error) {
      setArticleNotice(error.message);
      return;
    }

    setArticleNotice("Article updated.");
    cancelEditing();
    await loadArticles();
  };

  const handleDeleteArticle = async (article) => {
    const confirmed = window.confirm(`Delete "${article.article_title}"? This cannot be undone.`);
    if (!confirmed) return;

    setArticleNotice("");

    const images = (article.article_image ?? [])
      .slice()
      .sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0));

    if (images.length) {
      const storagePaths = images
        .map((image) => image.image?.storage_path)
        .filter(Boolean);
      await supabase.storage.from(ARTICLE_BUCKET).remove(storagePaths);
      await supabase.from("article_image").delete().eq("article_id", article.article_id);
      await supabase
        .from("image")
        .delete()
        .in(
          "image_id",
          images.map((image) => image.image_id)
        );
    }

    const { error } = await supabase.from("article").delete().eq("article_id", article.article_id);
    if (error) {
      setArticleNotice(error.message);
      return;
    }

    setArticleNotice("Article removed.");
    await loadArticles();
  };

  if (session) {
    return (
      <div style={{ display: "flex", minHeight: "calc(100vh - 160px)" }}>
        {/* Sidebar */}
        <div
          style={{
            width: sidebarOpen ? "25%" : "50px",
            minWidth: sidebarOpen ? "280px" : "50px",
            maxWidth: sidebarOpen ? "400px" : "50px",
            backgroundColor: "#f8f9fa",
            borderRight: "1px solid #e0e0e0",
            transition: "all 0.3s ease",
            overflow: "hidden",
            overflowY: "auto",
          }}
        >
          <div style={{ padding: sidebarOpen ? "20px" : "10px" }}>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{
                width: "100%",
                padding: sidebarOpen ? "6px 12px" : "6px",
                backgroundColor: "transparent",
                color: CARDINAL,
                border: `1px solid ${CARDINAL}`,
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: sidebarOpen ? "13px" : "12px",
                marginBottom: sidebarOpen ? "16px" : "0",
              }}
            >
              {sidebarOpen ? "◀ Hide" : "▶"}
            </button>

            {sidebarOpen && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                  <h4 style={{ marginBottom: 0, fontSize: "1.3rem" }}>Your Articles</h4>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={loadArticles}
                    type="button"
                    style={{ fontSize: "12px", padding: "4px 8px" }}
                  >
                    Refresh
                  </button>
                </div>

                {articlesLoading ? (
                  <p style={{ fontSize: "14px" }}>Loading...</p>
                ) : articles.length === 0 ? (
                  <p style={{ fontSize: "14px", color: "#6b6b6b" }}>No articles yet.</p>
                ) : (
                  <div style={{ display: "grid", gap: 12 }}>
                    {articles.map((article) => {
                      const isEditing = editingId === article.article_id;
                      return (
                        <div
                          key={article.article_id}
                          style={{
                            border: "1px solid #e4e4e4",
                            borderRadius: 8,
                            padding: "12px",
                            backgroundColor: "#fff",
                          }}
                        >
                          {isEditing ? (
                            <>
                              <input
                                className="form-control mb-2"
                                value={editTitle}
                                onChange={(event) => setEditTitle(event.target.value)}
                                style={{ fontSize: "13px" }}
                              />
                              <textarea
                                className="form-control mb-2"
                                rows={2}
                                value={editDescription}
                                onChange={(event) => setEditDescription(event.target.value)}
                                style={{ fontSize: "13px" }}
                              />
                              <div style={{ display: "flex", gap: 6 }}>
                                <button
                                  className="btn btn-sm btn-primary"
                                  type="button"
                                  onClick={() => handleUpdateArticle(article.article_id)}
                                  style={{ fontSize: "12px", padding: "4px 8px" }}
                                >
                                  Save
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  type="button"
                                  onClick={cancelEditing}
                                  style={{ fontSize: "12px", padding: "4px 8px" }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              <h6 style={{ marginBottom: 4, fontSize: "14px", fontWeight: 600 }}>
                                {article.article_title}
                              </h6>
                              <p style={{ marginBottom: 4, fontSize: "12px", color: "#4a4a4a" }}>
                                {article.article_description?.substring(0, 60)}{article.article_description?.length > 60 ? "..." : ""}
                              </p>
                              <small style={{ color: "#8c8c8c", fontSize: "11px" }}>
                                {article.article_date ? new Date(article.article_date).toLocaleDateString() : "No date"}
                              </small>
                              <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  type="button"
                                  onClick={() => startEditing(article)}
                                  style={{ fontSize: "11px", padding: "3px 8px" }}
                                >
                                  Edit
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  type="button"
                                  onClick={() => handleDeleteArticle(article)}
                                  style={{ fontSize: "11px", padding: "3px 8px" }}
                                >
                                  Delete
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, padding: "40px 20px", overflowY: "auto" }}>
          <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <div>
                <h2 style={{ marginBottom: 6 }}>Dashboard</h2>
                <p style={{ marginBottom: 0, color: "#3f3f3f" }}>Signed in as {session.user.email}</p>
              </div>
              <button className="btn btn-secondary" onClick={signOut}>
                Sign out
              </button>
            </div>

            <div className="card p-4" style={{ borderRadius: 16 }}>
          <h4 style={{ marginBottom: 12 }}>Upload a new article</h4>
          
          <div style={{ display: "flex", gap: "10px", marginBottom: 20 }}>
            <button
              type="button"
              style={tabButtonStyle(uploadMode === "image")}
              onClick={() => setUploadMode("image")}
            >
              Image Article
            </button>
            <button
              type="button"
              style={tabButtonStyle(uploadMode === "text")}
              onClick={() => setUploadMode("text")}
            >
              Text Article
            </button>
          </div>

          <p style={{ marginBottom: 20, color: "#4d4d4d" }}>
            {uploadMode === "image" 
              ? "Add a title and description, then attach a zipped article package."
              : "Add a title, description, and paste or type your article content."}
          </p>
          
          <form onSubmit={uploadMode === "image" ? handleCreateArticle : handleCreateTextArticle}>
            <div className="form-group mb-3">
              <label htmlFor="article-title" style={{ fontWeight: 600 }}>
                Title
              </label>
              <input
                id="article-title"
                type="text"
                className="form-control"
                placeholder="Article title"
                value={articleTitle}
                onChange={(event) => setArticleTitle(event.target.value)}
                disabled={uploading}
                required
                style={{ minHeight: "48px" }}
              />
            </div>

            <div className="form-group mb-3">
              <label htmlFor="article-description" style={{ fontWeight: 600 }}>
                Description
              </label>
              <textarea
                id="article-description"
                className="form-control"
                rows={4}
                placeholder="Short summary for editors and reviewers"
                value={articleDescription}
                onChange={(event) => setArticleDescription(event.target.value)}
                disabled={uploading}
                required
              />
            </div>

            {uploadMode === "image" ? (
              <div className="form-group mb-4">
                <label htmlFor="article-file" style={{ fontWeight: 600 }}>
                  Zipped file
                </label>
                <input
                  id="article-file"
                  type="file"
                  className="form-control"
                  accept=".zip,application/zip,application/x-zip-compressed"
                  onChange={(event) => setArticleFile(event.target.files?.[0] ?? null)}
                  disabled={uploading}
                  required
                />
                <small style={{ display: "block", marginTop: 8, color: "#6b6b6b" }}>
                  {uploading
                    ? "Uploading..."
                    : articleNotice
                    ? articleNotice
                    : "Add your zipped folder of images"}
                </small>
              </div>
            ) : (
              <>
                <div className="form-group mb-3">
                  <label style={{ fontWeight: 600, marginBottom: 12, display: "block" }}>
                    Article Content
                  </label>
                  <small style={{ display: "block", marginBottom: 16, color: "#6b6b6b" }}>
                    Drag blocks to reorder. Text blocks support HTML and Markdown.
                  </small>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                    onChange={handleImageUpload}
                    disabled={uploading || uploadingImage}
                    style={{ display: "none" }}
                    id="image-upload-input"
                  />

                  <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    {contentBlocks.map((block, index) => (
                      <div
                        key={block.id}
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragEnd={handleDragEnd}
                        style={{
                          border: "2px solid #e0e0e0",
                          borderRadius: "12px",
                          padding: "16px",
                          backgroundColor: draggedBlockIndex === index ? "#f5f5f5" : "#fff",
                          cursor: "move",
                          transition: "all 0.2s ease",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                          <span style={{ fontWeight: 600, color: "#666", fontSize: "14px" }}>
                            {block.type === "text" ? "📝 Text Block" : "🖼️ Image Block"} #{index + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => deleteBlock(block.id)}
                            disabled={uploading}
                            style={{
                              padding: "4px 12px",
                              backgroundColor: "#fff",
                              border: "1px solid #d32f2f",
                              borderRadius: "6px",
                              color: "#d32f2f",
                              fontWeight: 600,
                              cursor: uploading ? "not-allowed" : "pointer",
                              fontSize: "12px",
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </div>

                        {block.type === "text" ? (
                          <textarea
                            className="form-control"
                            rows={6}
                            placeholder="Enter text content (supports HTML and Markdown)..."
                            value={block.content}
                            onChange={(e) => updateBlockContent(block.id, e.target.value)}
                            disabled={uploading}
                            style={{ fontSize: "14px", lineHeight: "1.6" }}
                          />
                        ) : (
                          <div style={{ textAlign: "center" }}>
                            <img
                              src={block.url}
                              alt="Article content"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "300px",
                                borderRadius: "8px",
                                objectFit: "contain",
                              }}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {articleNotice && (
                    <small style={{ display: "block", marginTop: 16, color: articleNotice.includes("success") ? "#2e7d32" : "#d32f2f" }}>
                      {articleNotice}
                    </small>
                  )}
                </div>

                <div style={{ display: "flex", gap: "12px", marginBottom: 16, justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    onClick={addTextBlock}
                    disabled={uploading}
                    style={{
                      padding: "10px 20px",
                      backgroundColor: "#fff",
                      border: `2px solid ${CARDINAL}`,
                      borderRadius: "8px",
                      color: CARDINAL,
                      fontWeight: 600,
                      cursor: uploading ? "not-allowed" : "pointer",
                      opacity: uploading ? 0.6 : 1,
                    }}
                  >
                    ➕ Add Text Block
                  </button>
                  <button
                    type="button"
                    onClick={() => document.getElementById("image-upload-input").click()}
                    disabled={uploading || uploadingImage}
                    style={{
                      padding: "10px 20px",
                      backgroundColor: "#fff",
                      border: `2px solid ${CARDINAL}`,
                      borderRadius: "8px",
                      color: CARDINAL,
                      fontWeight: 600,
                      cursor: uploading || uploadingImage ? "not-allowed" : "pointer",
                      opacity: uploading || uploadingImage ? 0.6 : 1,
                    }}
                  >
                    {uploadingImage ? "Uploading..." : "📷 Add Image"}
                  </button>
                </div>
              </>
            )}

            <div style={{ display: "flex", justifyContent: "center" }}>
              <button
                className="btn"
                type="submit"
                disabled={uploading}
                style={{
                  background: CARDINAL,
                  color: "#fff",
                  fontWeight: 700,
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: "999px",
                }}
              >
                {uploading ? "Uploading..." : uploadMode === "image" ? "Upload image article" : "Upload text article"}
              </button>
            </div>
          </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "calc(100vh - 160px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "40px 16px 56px",
      }}
    >
      <div
        className="container"
        style={{
          maxWidth: "820px",
          background: "#fff",
          padding: "48px 48px 56px",
          borderRadius: "20px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.14)",
          margin: "0 auto",
        }}
      >
      <h2
        style={{
          fontWeight: 800,
          marginBottom: 18,
          textAlign: "center",
          fontSize: "2.2rem",
          color: CARDINAL,
        }}
      >
        {activeTab === "login" ? "Sign In" : "Create Account"}
      </h2>

      <div style={{ display: "flex", gap: "10px", margin: "18px 0", justifyContent: "center" }}>
        <button
          type="button"
          style={tabButtonStyle(activeTab === "login")}
          onClick={() => setActiveTab("login")}
        >
          Log in
        </button>
        <button
          type="button"
          style={tabButtonStyle(activeTab === "signup")}
          onClick={() => setActiveTab("signup")}
        >
          Create account
        </button>
      </div>

      <form onSubmit={handleAuth} style={{ marginTop: 12 }}>
        <div className="form-group mb-3">
          <label
            htmlFor="auth-email"
            style={{ fontSize: "1.2rem", letterSpacing: "0.05em", color: "#2f2a28" }}
          >
            Email
          </label>
          <input
            id="auth-email"
            type="email"
            className="form-control"
            placeholder="you@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{ minHeight: "48px", fontSize: "1.05rem", padding: "12px 14px" }}
          />
        </div>

        <div className="form-group mb-3">
          <label
            htmlFor="auth-password"
            style={{ fontSize: "1.2rem", letterSpacing: "0.05em", color: "#2f2a28" }}
          >
            Password
          </label>
          <input
            id="auth-password"
            type="password"
            className="form-control"
            placeholder="At least 6 characters"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={{ minHeight: "48px", fontSize: "1.05rem", padding: "12px 14px" }}
          />
        </div>

        <button
          className="btn w-100"
          type="submit"
          disabled={loading}
          style={{
            background: CARDINAL,
            color: "#fff",
            fontWeight: 700,
            border: "none",
            padding: "12px",
            boxShadow: "0 12px 24px rgba(0,0,0,0.12)",
            fontSize: "1.05rem",
          }}
        >
          {loading ? "Processing..." : activeTab === "login" ? "Log in" : "Create account"}
        </button>
      </form>

      {message && (
        <p
          className="mt-3"
          style={{
            fontWeight: 600,
            color: "#1f2d4f",
            letterSpacing: "0.05em",
            fontSize: "1.05rem",
          }}
        >
          {message}
        </p>
      )}

      {activeTab === "login" && (
        <div className="mt-4" style={{ textAlign: "center" }}>
          <p
            style={{
              marginBottom: 10,
              letterSpacing: "0.05em",
              color: "#2f2a28",
              fontSize: "1.05rem",
            }}
          >
            Forgot password?
          </p>
          <button
            type="button"
            className="btn"
            onClick={handleResetPassword}
            disabled={loading}
            style={{
              background: "#fff",
              border: `1px solid ${CARDINAL}`,
              color: CARDINAL,
              fontWeight: 700,
              padding: "10px 16px",
              borderRadius: "999px",
              boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
              fontSize: "1.05rem",
              letterSpacing: "0.05em",
            }}
          >
            Email me a reset link
          </button>
        </div>
      )}
      </div>
    </div>
  );
}

export default Dashboard;
